import { getLanguageFromStorage } from 'syf-js-utilities';
import useSteps from '../../context/steps.context';
import useTypesafeAnswer from '../../hooks/useTypesafeAnswer';
import { createLocalDate, minDate, parseLocalDate } from '../../helpers/formatDate';
import useConfig from '../../context/config.context';
import useAccountActivity from '../useAccountActivity';
import getErrorAnalytics from './getErrorAnalytics';
import useAccountSummary from '../useAccountSummary';
const getHandlerTextKeys = (step, { hasTransactions }) => {
    const nextText = 'common.next';
    const backText = 'common.back';
    switch (step) {
        case 'ReasonSelectionLostCard':
        case 'ReasonSelection':
            return {
                primaryTextKey: nextText,
                secondaryTextKey: 'common.cancel'
            };
        case 'SelectAccount':
        case 'RecognizeTransactions':
            if (hasTransactions)
                return {
                    primaryTextKey: 'recognizeTransaction.yesRecognize',
                    secondaryTextKey: 'recognizeTransaction.noDontRecognize'
                };
            return {
                primaryTextKey: nextText,
                secondaryTextKey: backText
            };
        case 'SelectCardholder':
        case 'WhenLost':
            return {
                primaryTextKey: nextText,
                secondaryTextKey: backText
            };
        case 'ReviewDamagedCard':
        case 'ReviewMisplacedCard':
        case 'ReviewNeverReceived':
            return {
                primaryTextKey: 'common.submit',
                secondaryTextKey: backText
            };
        case 'Confirmation':
        case 'TempBlockWithNumber':
            return {
                primaryTextKey: 'common.done'
            };
        case 'CantProcess':
        case 'CantVerify':
        case 'UnableSubmit':
        case 'APIError':
            return {
                primaryTextKey: 'common.close'
            };
        case 'CloseConfirmation':
            return {
                primaryTextKey: 'closeConfirmation.yesCancelRequest',
                secondaryTextKey: 'closeConfirmation.noDontCancel'
            };
        case 'ExitConfirmation':
            return {
                primaryTextKey: 'exitConfirmation.changeAddress',
                secondaryTextKey: 'exitConfirmation.goBack'
            };
        default: {
            /*
             * Throw a compile-time error in case we forgot to handle a Step case.
             * See more: https://www.typescriptlang.org/docs/handbook/2/narrowing.html#exhaustiveness-checking
             */
            const exhaustiveCheck = step;
            return exhaustiveCheck;
        }
    }
};
const defaultAnalytics = {
    dataObject: 'button',
    dataType: 'replacement card',
    dataAnalytics: 'clicktrackingevent'
};
const getHandlerDataAnalytics = (step, values) => {
    var _a;
    const cleanReason = (_a = values.replacementReason) === null || _a === void 0 ? void 0 : _a.toLowerCase().replace('-', '');
    const reasonString = cleanReason ? `:${cleanReason}card` : '';
    switch (step) {
        case 'ReasonSelection': {
            return {
                primaryAnalytics: Object.assign(Object.assign({}, defaultAnalytics), { dataType: `replacement card${reasonString}`, dataReason: 'next' }),
                secondaryAnalytics: Object.assign(Object.assign({}, defaultAnalytics), { dataReason: 'cancel' })
            };
        }
        case 'SelectCardholder': {
            return {
                primaryAnalytics: Object.assign(Object.assign({}, defaultAnalytics), { 
                    /* TODO: pending analytics */
                    dataType: ``, dataReason: 'next' }),
                secondaryAnalytics: Object.assign(Object.assign({}, defaultAnalytics), { dataReason: 'back' })
            };
        }
        case 'SelectAccount': {
            return {
                primaryAnalytics: Object.assign(Object.assign({}, defaultAnalytics), { dataType: `replacement card`, dataReason: 'next' }),
                secondaryAnalytics: Object.assign(Object.assign({}, defaultAnalytics), { dataReason: 'back' })
            };
        }
        case 'ReviewNeverReceived':
        case 'ReviewMisplacedCard':
        case 'ReviewDamagedCard':
            return {
                primaryAnalytics: Object.assign(Object.assign({}, defaultAnalytics), { dataReason: 'submit' }),
                secondaryAnalytics: Object.assign(Object.assign({}, defaultAnalytics), { dataReason: 'back' })
            };
        case 'ExitConfirmation':
            return {
                primaryAnalytics: Object.assign(Object.assign({}, defaultAnalytics), { dataReason: 'change address' }),
                secondaryAnalytics: Object.assign(Object.assign({}, defaultAnalytics), { dataReason: 'go back' })
            };
        case 'Confirmation':
            return {
                primaryAnalytics: Object.assign(Object.assign({}, defaultAnalytics), { dataReason: 'done' })
            };
        case 'CloseConfirmation':
            return {
                primaryAnalytics: Object.assign(Object.assign({}, defaultAnalytics), { dataReason: 'yes cancel request' }),
                secondaryAnalytics: Object.assign(Object.assign({}, defaultAnalytics), { dataReason: 'no dont cancel' })
            };
        case 'APIError':
        case 'UnableSubmit':
        case 'CantProcess':
        case 'CantVerify':
            return {
                primaryAnalytics: Object.assign(Object.assign({}, defaultAnalytics), { dataReason: 'close' })
            };
        case 'RecognizeTransactions':
            return {
                primaryAnalytics: Object.assign(Object.assign({}, defaultAnalytics), { dataReason: values.hasTransactions ? 'yes i recognize' : 'next' }),
                secondaryAnalytics: Object.assign(Object.assign({}, defaultAnalytics), { dataReason: values.hasTransactions ? 'no i dont recognize' : 'back' })
            };
        case 'WhenLost':
        case 'TempBlockWithNumber':
            return { primaryAnalytics: {} };
        case 'ReasonSelectionLostCard': {
            /* TODO: pending analytics */
            return {
                primaryAnalytics: Object.assign(Object.assign({}, defaultAnalytics), { dataType: `lost stolen card${reasonString}`, dataReason: 'next' }),
                secondaryAnalytics: Object.assign(Object.assign({}, defaultAnalytics), { dataReason: 'cancel' })
            };
        }
        default: {
            const exhaustiveCheck = step;
            return exhaustiveCheck;
        }
    }
};
export const getHandlerActions = (step) => {
    switch (step) {
        case 'ReasonSelection':
        case 'ReasonSelectionLostCard':
            return {
                primaryAction: { type: 'nextStep' },
                secondaryAction: { type: 'confirmClose' }
            };
        case 'ReviewDamagedCard':
            return {
                primaryAction: { type: 'performRequest' },
                secondaryAction: { type: 'previousStep' }
            };
        case 'SelectAccount':
        case 'SelectCardholder':
        case 'ReviewNeverReceived':
        case 'ReviewMisplacedCard':
        case 'RecognizeTransactions':
        case 'WhenLost':
            return {
                primaryAction: { type: 'nextStep' },
                secondaryAction: { type: 'previousStep' }
            };
        case 'Confirmation':
        case 'CantVerify':
        case 'CantProcess':
        case 'UnableSubmit':
        case 'APIError':
        case 'TempBlockWithNumber':
            return {
                primaryAction: { type: 'closeFlow' }
            };
        case 'CloseConfirmation':
        case 'ExitConfirmation':
            return {
                primaryAction: { type: 'closeFlow' },
                secondaryAction: { type: 'previousStep' }
            };
        default: {
            const exhaustiveCheck = step;
            return exhaustiveCheck;
        }
    }
};
/* Helper function to validate when the different types of answers are valid or not. */
export const isValidAnswer = (answer) => {
    if (!answer || answer.value.length === 0) {
        return false;
    }
    switch (answer.step) {
        case 'ReasonSelection':
        case 'ReasonSelectionLostCard':
        case 'SelectAccount':
        case 'SelectCardholder':
            return !!answer.value.length;
        case 'WhenLost': {
            const selectedDate = parseLocalDate(answer.value);
            if (!selectedDate || Number.isNaN(selectedDate.getTime()))
                return false;
            const today = createLocalDate();
            return (selectedDate.getTime() >= minDate.getTime() &&
                selectedDate.getTime() <= today.getTime());
        }
        default:
            return false;
    }
};
const useHandlers = () => {
    var _a;
    const { stepsState: { stepsList, activeStep, answers, error }, stepsDispatch } = useSteps();
    const { triggerAnalyticsEvent } = useConfig();
    const replacementReason = (_a = useTypesafeAnswer('ReasonSelection').currentAnswer) === null || _a === void 0 ? void 0 : _a.value;
    const currentLang = getLanguageFromStorage();
    const { transactions } = useAccountActivity(currentLang);
    const { isSemu = false } = useAccountSummary();
    const currentFlow = activeStep === 'ReasonSelection'
        ? 'ReasonSelection'
        : 'ReasonSelectionLostCard';
    const { currentAnswer: reasonSelectionAnswer } = useTypesafeAnswer(currentFlow);
    const { primaryTextKey, secondaryTextKey } = getHandlerTextKeys(activeStep, {
        hasTransactions: transactions.length > 0
    });
    const { primaryAnalytics, secondaryAnalytics } = getHandlerDataAnalytics(activeStep, {
        replacementReason: reasonSelectionAnswer === null || reasonSelectionAnswer === void 0 ? void 0 : reasonSelectionAnswer.value,
        hasTransactions: transactions.length > 0
    });
    const primaryHandler = () => {
        const { primaryAction } = getHandlerActions(activeStep);
        const stepNodes = stepsList[activeStep];
        const requiresAnswer = stepNodes.some(node => node.answer);
        const currentAnswer = answers.find(answer => answer.step === activeStep);
        const errorAnalytics = getErrorAnalytics(activeStep, {
            isSemu,
            replacementReason
        });
        if (requiresAnswer && !isValidAnswer(currentAnswer)) {
            stepsDispatch({
                type: 'throwError'
            });
            if (errorAnalytics) {
                triggerAnalyticsEvent(errorAnalytics);
            }
            return;
        }
        stepsDispatch(primaryAction);
    };
    const secondaryHandler = () => {
        const { secondaryAction } = getHandlerActions(activeStep);
        if (error) {
            stepsDispatch({ type: 'clearError' });
        }
        if (secondaryAction) {
            stepsDispatch(secondaryAction);
        }
    };
    return {
        primaryHandler,
        primaryTextKey,
        primaryAnalytics,
        secondaryHandler,
        secondaryTextKey,
        secondaryAnalytics
    };
};
export default useHandlers;
//# sourceMappingURL=index.js.map